<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import { isValidDate, formatDateForApi } from '@/shared/lib';
import { UiModalHeader, ColoredButton } from '@/shared/ui';
import UserFormContent from './UserFormContent.vue';
import type { PositionItem, FunctionalActivityItem } from './UserFormContent.vue';
import {
  useDepartmentOptions,
  useFunctionalActivityOptions,
  useUserManagement,
  useEditUserFormValidation,
  resolveUserDismissalState,
  usePositionOptionsCache,
  useEditUserData,
} from '../lib';
import { RoleEnum } from '@/entities/User';

const props = defineProps<{
  userId: number;
  showSuperadminToggle?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save'): void;
}>();

const name = ref('');
const registrationNumber = ref('');
const username = ref('');
const email = ref('');
const positions = ref<PositionItem[]>([{ departmentId: '', positionId: '' }]);
const functionalActivities = ref<FunctionalActivityItem[]>([{ functionalActivityId: '' }]);
const admissionDate = ref<Date | null>(null);
const dismissalDate = ref<Date | null>(null);
const status = ref('ACTIVE');
const isSuperadmin = ref(false);
const isLoadingUser = ref(false);

const { isLoadingPositions, loadPositionsForDepartment, getPositionOptions } = usePositionOptionsCache();

const { updateUser, isUpdating } = useUserManagement();

const {
  departmentOptions,
  isLoading: isLoadingDepartments,
  loadDepartments,
} = useDepartmentOptions();

const {
  functionalActivityOptions,
  isLoading: isLoadingActivities,
  loadFunctionalActivities,
} = useFunctionalActivityOptions();

const {
  serverEmailError,
  serverRegistrationNumberError,
  serverUsernameError,
  registrationNumberError,
  emailError,
  admissionDateError,
  dismissalDateError,
  usernameError,
  hasFormChanges,
  isSaveDisabled,
  isHydratingUserData,
  initialFormSnapshot,
  buildFormSnapshot,
  extractFieldErrorMessage,
} = useEditUserFormValidation({
  name,
  username,
  email,
  registrationNumber,
  admissionDate,
  dismissalDate,
  status,
  isSuperadmin,
  functionalActivities,
  isUpdating,
  isLoadingUser,
});

const { loadUserData } = useEditUserData({
  name,
  username,
  email,
  registrationNumber,
  admissionDate,
  dismissalDate,
  status,
  isSuperadmin,
  functionalActivities,
  isLoadingUser,
  isHydratingUserData,
  loadDepartments,
  loadFunctionalActivities,
  onUserLoaded: () => {
    initialFormSnapshot.value = buildFormSnapshot();
  },
});

const transformedDepartmentOptions = computed(() =>
  departmentOptions.value.map(opt => ({ id: opt.value, name: opt.label }))
);

watch(
  () => props.userId,
  (userId) => {
    if (!userId) return;
    loadUserData(userId);
  },
  { immediate: true }
);

const handleSave = async () => {
  if (isSaveDisabled.value) return;
  const trimmedRegistrationNumber = registrationNumber.value.trim();
  const resolvedDismissalState = resolveUserDismissalState(
    admissionDate.value,
    dismissalDate.value,
    status.value
  );

  const result = await updateUser(props.userId, {
    name: name.value.trim(),
    username: username.value.trim(),
    email: email.value.trim(),
    registrationNumber: trimmedRegistrationNumber,
    admissionDate: isValidDate(admissionDate.value) ? formatDateForApi(admissionDate.value) : null,
    dismissalDate: resolvedDismissalState.dismissalDate
      ? formatDateForApi(resolvedDismissalState.dismissalDate)
      : null,
    status: resolvedDismissalState.status as 'ACTIVE' | 'INACTIVE',
    role: isSuperadmin.value ? RoleEnum.SUPERUSER : RoleEnum.USER,
  });

  if (result.fieldErrors && result.fieldErrors.length > 0) {
    const usernameErr = extractFieldErrorMessage(result.fieldErrors, 'username');
    if (usernameErr) serverUsernameError.value = usernameErr;

    const emailErr = extractFieldErrorMessage(result.fieldErrors, 'email');
    if (emailErr) serverEmailError.value = emailErr;

    const regNumErr = extractFieldErrorMessage(result.fieldErrors, 'registrationNumber');
    if (regNumErr) serverRegistrationNumberError.value = regNumErr;
    return;
  }

  if (result.success) {
    emit('save');
  }
};

const handleClose = () => {
  emit('close');
};

const handleClosed = () => {
  name.value = '';
  registrationNumber.value = '';
  username.value = '';
  email.value = '';
  positions.value = [{ departmentId: '', positionId: '' }];
  functionalActivities.value = [{ functionalActivityId: '' }];
  admissionDate.value = null;
  dismissalDate.value = null;
  status.value = 'ACTIVE';
  isSuperadmin.value = false;
  initialFormSnapshot.value = null;
  serverUsernameError.value = '';
  serverEmailError.value = '';
  serverRegistrationNumberError.value = '';
};
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[1000px] max-h-[90vh] shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @closed="handleClosed"
  >
    <div class="overflow-hidden flex flex-col max-h-[90vh]">
      <UiModalHeader
        class="px-6 py-4 text-sm border-b border-gray-02 flex-shrink-0"
        title="Editar Usuário"
        @close="handleClose"
      />

      <div
        v-if="isLoadingUser"
        class="flex-1 flex items-center justify-center py-12"
      >
        <span class="text-sm text-gray-05">Carregando...</span>
      </div>

      <div
        v-else
        class="overflow-y-auto flex-1"
      >
        <UserFormContent
          :name="name"
          :registration-number="registrationNumber"
          :username="username"
          :email="email"
          :positions="positions"
          :functional-activities="functionalActivities"
          :admission-date="admissionDate"
          :dismissal-date="dismissalDate"
          :status="status"
          :is-superadmin="isSuperadmin"
          :show-superadmin-toggle="props.showSuperadminToggle ?? false"
          :department-options="transformedDepartmentOptions"
          :get-position-options="getPositionOptions"
          :functional-activity-options="functionalActivityOptions"
          :is-loading-departments="isLoadingDepartments"
          :is-loading-positions="isLoadingPositions"
          :is-loading-activities="isLoadingActivities"
          :username-error="usernameError"
          :email-error="emailError"
          :admission-date-error="admissionDateError"
          :dismissal-date-error="dismissalDateError"
          :registration-number-error="registrationNumberError"
          @update:name="name = $event"
          @update:positions="positions = $event"
          @update:registration-number="registrationNumber = $event"
          @update:username="username = $event"
          @update:email="email = $event"
          @update:functional-activities="functionalActivities = $event"
          @update:admission-date="admissionDate = $event"
          @update:dismissal-date="dismissalDate = $event"
          @update:status="status = $event"
          @update:is-superadmin="isSuperadmin = $event"
          @load-positions="loadPositionsForDepartment"
        />
      </div>

      <div class="px-6 py-4 flex justify-end gap-3 border-t border-gray-02 flex-shrink-0">
        <ColoredButton
          text="Cancelar"
          outlined
          color="gray-03"
          :disabled="isUpdating"
          @click="handleClose"
        />
        <ColoredButton
          :disabled="isSaveDisabled"
          :text="isUpdating ? 'Salvando...' : 'Salvar'"
          @click="handleSave"
        />
      </div>
    </div>
  </VueFinalModal>
</template>

<style scoped>
.vfm__overlay {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
